import { useCallback, useEffect, useMemo, useState } from 'react'
import { useEquipmentScope } from '../equipmentScope'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, Star } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { EquipmentLegend } from '@/components/equipment/EquipmentLegend'
import { CalendarDays } from 'lucide-react'
import { PlannerCalendar, type PlannerCalendarEvent, type PlannerView } from './PlannerCalendar'
import { localDayIso } from '@/lib/timeWindow'
import { DayEventsDialog, OccurrenceDetailsDialog } from './OccurrenceDialogs'
import { formatOccurrenceTime, type PlannerOccurrence } from '@/lib/equipmentOccurrences'
import { useOccurrences } from '@/hooks/useOccurrences'
import type { EventTypeRow } from '@/hooks/useEventTypes'
import type { EquipmentTypeRow, ResolveEventMeta } from '@/types/equipment'
import { equipmentApi, REPEAT_CYCLE_LABELS, type RepeatCycle } from '@/api/equipmentApi'
import { useTenant } from '@/providers/TenantProvider'
import { PERMISSIONS, usePermission } from '@/lib/permissions'

const ALL = 'all'

// Quick jumps. `date` is a thunk so each click reads the current clock rather
// than a value frozen at module load — a tab left open overnight would
// otherwise send "This week" to yesterday's week.
const DATE_JUMPS: { label: string; date: () => Date; view: PlannerView }[] = [
  { label: 'This week', date: () => new Date(), view: 'dayGridWeek' },
  { label: 'This month', date: () => new Date(), view: 'dayGridMonth' },
  {
    label: 'Next month',
    date: () => {
      const d = new Date()
      // Anchor to the 1st first: stepping a month from the 31st lands two
      // months out and silently skips one.
      d.setDate(1)
      d.setMonth(d.getMonth() + 1)
      return d
    },
    view: 'dayGridMonth',
  },
]

export function EquipmentCalendarTab({
  types,
  eventTypes,
  resolveMeta,
}: {
  types: EquipmentTypeRow[]
  eventTypes: EventTypeRow[]
  resolveMeta: ResolveEventMeta
}) {
  const { purposes, copy } = useEquipmentScope()
  const allCodes = useMemo(() => eventTypes.map((t) => t.code), [eventTypes])
  const [group, setGroup] = useState<string>(ALL)
  const [range, setRange] = useState<{ start: string; end: string; title: string } | null>(null)
  const [activeTypes, setActiveTypes] = useState<string[] | null>(null)
  const [showNonRoutine, setShowNonRoutine] = useState(true)
  const [selected, setSelected] = useState<PlannerOccurrence | null>(null)
  const [dayOpen, setDayOpen] = useState<string | null>(null)
  const [focusDate, setFocusDate] = useState<string | null>(null)
  const [focusView, setFocusView] = useState<PlannerView | null>(null)

  // Re-jumping to the SAME date must still move the calendar (e.g. "This month"
  // after paging away), and identical state wouldn't re-fire the effect — so
  // the value is cleared first and re-applied on the next tick.
  const jumpTo = (date: string, view: PlannerView | null) => {
    if (!date) return
    setFocusDate(null)
    setFocusView(null)
    requestAnimationFrame(() => {
      setFocusView(view)
      setFocusDate(date)
    })
  }

  // Until the catalog loads, `activeTypes` is null → treat all codes as active.
  const activeCodes = activeTypes ?? allCodes

  const handleRangeChange = useCallback((start: string, end: string, title: string) => {
    setRange({ start, end, title })
  }, [])

  const toggleType = (code: string) =>
    setActiveTypes((prev) => {
      const base = prev ?? allCodes
      return base.includes(code) ? base.filter((c) => c !== code) : [...base, code]
    })

  const filtersDirty = activeCodes.length !== allCodes.length || !showNonRoutine

  const groupNames = useMemo(
    () => [...new Set(types.map((t) => t.group))].sort(),
    [types],
  )

  // Occurrences are expanded server-side for the visible range; the group filter
  // stays client-side so switching groups doesn't refetch.
  // This page's share only, and Generate below likewise: generating from the
  // Cleaning page creates room cleaning and nothing else (z8nrz7ewt2).
  const { data: apiOccurrences } = useOccurrences(range, undefined, purposes)

  // Materialises the occurrences above into assigned per-unit MaintenanceEvents
  // for the visible range (z8nrz7dyfp) — the calendar only previews the
  // schedule; nothing wired an occurrence to a worklist task before this.
  const { selectedTenantId } = useTenant()
  const { can } = usePermission()
  const canGenerate = can(PERMISSIONS.equipmentManage)
  const queryClient = useQueryClient()
  const generateMutation = useMutation({
    mutationFn: () => {
      if (!selectedTenantId || !range) throw new Error('No tenant or range selected')
      return equipmentApi.generateEvents(selectedTenantId, { from: range.start, to: range.end, purposes })
    },
    // Only the persisted events feed changes — occurrences are a computed
    // preview of the schedule and are unaffected by generation.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['maintenance-events'] })
    },
  })
  const { reset: resetGenerateMutation } = generateMutation
  // A stale "N generated" / error from the PREVIOUS range must not linger once
  // the visible range changes — otherwise paging to a new month reads as if it
  // had already been generated.
  useEffect(() => {
    resetGenerateMutation()
  }, [range?.start, range?.end, resetGenerateMutation])

  const occurrences = useMemo<PlannerOccurrence[]>(
    () =>
      apiOccurrences.map((o) => ({
        id: o.id,
        date: o.date,
        equipmentType: o.equipmentType,
        group: o.group,
        eventType: o.eventType,
        label: o.label,
        // Prefer the backend-provided color; fall back to the catalog lookup by code.
        color: o.color ?? resolveMeta(o.eventType).color,
        nonRoutine: !o.routine,
        note: o.note,
        startTime: o.startTime,
        endTime: o.endTime,
        // Set only on a repeating task's paint — carried through so the dialog
        // can tell a real work order from a preview of one.
        eventId: o.eventId,
        equipmentId: o.equipmentId,
        equipmentLabel: o.equipmentLabel,
        repeatCycle: o.repeatCycle,
      })),
    [apiOccurrences, resolveMeta],
  )

  const visibleOccurrences = useMemo(
    () => (group === ALL ? occurrences : occurrences.filter((o) => o.group === group)),
    [occurrences, group],
  )

  const filteredOccurrences = useMemo(
    () =>
      visibleOccurrences.filter((occ) =>
        occ.nonRoutine ? showNonRoutine : activeCodes.includes(occ.eventType),
      ),
    [visibleOccurrences, activeCodes, showNonRoutine],
  )

  // Process-step bookings deliberately do NOT appear here. This board is the
  // maintenance occurrence view; bookings live on the Team Schedule, where they
  // sit against the person who made them. Keeping them off this board stops the
  // two views from saying the same thing twice.

  const events = useMemo<PlannerCalendarEvent[]>(
    () => [
      ...filteredOccurrences.map((occ) => {
        const time = formatOccurrenceTime(occ)
        // A repeating task names ONE machine, so it is titled by the unit and
        // marked ↻. Titling it by the equipment type — which is right for a
        // per-type schedule occurrence, where no task exists yet — made every
        // paint read identically to the hundreds of schedule occurrences
        // already on the month, so an operator could not find their own task.
        const isRepeat = !!occ.eventId
        const cycle = occ.repeatCycle
          ? REPEAT_CYCLE_LABELS[occ.repeatCycle as RepeatCycle] ?? occ.repeatCycle
          : null
        return {
          id: occ.id,
          date: occ.date,
          title: isRepeat ? `↻ ${occ.equipmentLabel ?? occ.equipmentType}` : occ.equipmentType,
          subtitle: isRepeat
            ? `${occ.label}${cycle ? ` · repeats ${cycle.toLowerCase()}` : ''}`
            : `${occ.label}${time ? ` · ${time}` : ''}`,
          color: occ.color,
          // High-emphasis like a non-routine event: a repeat is a real work
          // order sitting among previews of work that does not exist yet.
          solid: occ.nonRoutine || isRepeat,
          star: occ.nonRoutine,
          meta: occ,
        }
      }),
    ],
    [filteredOccurrences],
  )

  const routineCount = filteredOccurrences.filter((o) => !o.nonRoutine).length
  const nonRoutineCount = filteredOccurrences.length - routineCount

  const dayOccurrences = useMemo(
    () => (dayOpen ? filteredOccurrences.filter((o) => o.date === dayOpen) : []),
    [filteredOccurrences, dayOpen],
  )

  const selectedUnits = selected
    ? types.find((t) => t.equipmentType === selected.equipmentType)?.units
    : undefined

  return (
    <div className='overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm'>
      {/* Interactive legend — click to filter event types */}
      <div className='flex flex-wrap items-center gap-x-2 gap-y-2 border-b border-gray-100 bg-gray-50/60 px-4 py-2'>
        <span className='text-[11px] font-semibold uppercase tracking-wide text-gray-400'>Filter</span>
        <EquipmentLegend eventTypes={eventTypes} activeCodes={activeCodes} onToggle={toggleType} />
        <button
          type='button'
          onClick={() => setShowNonRoutine((v) => !v)}
          title={showNonRoutine ? 'Hide non-routine events' : 'Show non-routine events'}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs transition-colors hover:bg-gray-100',
            showNonRoutine ? 'text-gray-700' : 'text-gray-400',
          )}
        >
          <Star className={cn('h-3 w-3', showNonRoutine ? 'text-pink-500' : 'text-gray-300')} />
          Calendar (non-routine)
        </button>
        {filtersDirty && (
          <button
            type='button'
            onClick={() => {
              setActiveTypes(null)
              setShowNonRoutine(true)
            }}
            className='ml-auto text-xs font-medium text-primary hover:underline'
          >
            Reset filters
          </button>
        )}
      </div>

      <div className='p-4'>
        <PlannerCalendar
          events={events}
          onRangeChange={handleRangeChange}
          onEventClick={(ev) => {
            // Reservations share the board but not the shape — the maintenance
            // details dialog would render nonsense for one, so it is not opened.
            if (ev.id.startsWith('reservation-')) return
            setSelected(ev.meta as PlannerOccurrence)
          }}
          onDateClick={setDayOpen}
          focusDate={focusDate}
          focusView={focusView}
          toolbarExtras={
            <div className='flex flex-wrap items-center gap-2'>
              {/* Jump straight to a date. The calendar's own prev/next still
                  own the visible range; this only re-anchors it, so the two
                  controls can't disagree about what is shown. */}
              <label className='flex items-center gap-1.5 rounded-lg border border-gray-200 px-2 py-1'>
                <CalendarDays className='h-4 w-4 shrink-0 text-gray-400' />
                <input
                  type='date'
                  aria-label='Jump to date'
                  value={focusDate ?? ''}
                  onChange={(e) => jumpTo(e.target.value, null)}
                  className='w-[130px] bg-transparent text-xs text-gray-700 outline-none'
                />
              </label>
              <div className='flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 p-0.5'>
                {DATE_JUMPS.map((j) => (
                  <button
                    key={j.label}
                    type='button'
                    onClick={() => jumpTo(localDayIso(j.date()), j.view)}
                    className='rounded-md px-2.5 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-white hover:text-gray-900'
                  >
                    {j.label}
                  </button>
                ))}
              </div>
              <Select value={group} onValueChange={setGroup}>
                <SelectTrigger className='h-8 w-[200px]'>
                  <SelectValue placeholder='All Groups' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All Groups</SelectItem>
                  {groupNames.map((g) => (
                    <SelectItem key={g} value={g}>
                      {g}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {canGenerate && (
                <>
                  <Button
                    type='button'
                    size='sm'
                    variant='outline'
                    className='h-8'
                    disabled={!range || generateMutation.isPending}
                    onClick={() => generateMutation.mutate()}
                  >
                    {generateMutation.isPending && (
                      <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />
                    )}
                    Generate Tasks{range ? ` for ${range.title}` : ''}
                  </Button>
                  {generateMutation.isSuccess && (
                    <span className='text-xs text-gray-500'>
                      {generateMutation.data.generated} generated
                      {generateMutation.data.skipped > 0
                        ? `, ${generateMutation.data.skipped} already existed`
                        : ''}
                    </span>
                  )}
                  {generateMutation.isError && (
                    <span className='text-xs text-rose-600'>
                      {generateMutation.error instanceof Error
                        ? generateMutation.error.message
                        : 'Generation failed'}
                    </span>
                  )}
                </>
              )}
            </div>
          }
          footer={
            <div className='flex flex-wrap items-center gap-2 text-xs text-gray-500'>
              <span>
                <span className='font-semibold text-gray-700'>{filteredOccurrences.length}</span>{' '}
                {copy.eventNoun} {filteredOccurrences.length === 1 ? 'event' : 'events'}
                {range ? ` in ${range.title}` : ''}
              </span>
              <span className='inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-600'>
                {routineCount} recurring
              </span>
              <span className='inline-flex items-center gap-1 rounded-full bg-pink-50 px-2 py-0.5 font-medium text-pink-700'>
                <Star className='h-3 w-3' />
                {nonRoutineCount} non-routine
              </span>
              <span className='text-gray-400'>
                Process bookings appear on the Team Schedule
              </span>
            </div>
          }
        />
      </div>

      <OccurrenceDetailsDialog
        occurrence={selected}
        units={selectedUnits}
        onClose={() => setSelected(null)}
      />
      <DayEventsDialog
        date={dayOpen}
        occurrences={dayOccurrences}
        onClose={() => setDayOpen(null)}
        onSelect={(occ) => {
          setDayOpen(null)
          setSelected(occ)
        }}
      />
    </div>
  )
}
